import { AgentInstructionSchema, AgentSchema, } from '@cloneai/shared-types';
import { AgentVoiceCaptureActionResponseSchema, AgentVoiceCaptureCreateRequestSchema, AgentVoiceCaptureCreateResponseSchema, AgentVoiceCloneActionResponseSchema, AgentVoiceCloneSetupRequestSchema, AgentVoiceCloneSetupResponseSchema, AgentVoiceCloneStatusResponseSchema, AgentDetailSchema, AgentGuardrailPromptUpdateRequestSchema, AgentKnowledgeConfigSchema, AgentKnowledgeConfigUpdateRequestSchema, AgentKnowledgeFileSourceUploadRequestSchema, AgentKnowledgeFileSourceUploadResponseSchema, AgentKnowledgeListResponseSchema, AgentKnowledgeSourceDetailResponseSchema, AgentKnowledgeSourceResponseSchema, AgentKnowledgeSourceUpdateRequestSchema, AgentKnowledgeTextSourceCreateRequestSchema, AgentMediaUploadResponseSchema, AgentMetricsResponseSchema, AgentSummarySchema, SessionDetailResponseSchema, SessionListResponseSchema, } from '@cloneai/shared-types/api';
import { z } from 'zod';
import { NoContentSchema } from '../_helpers.js';
// Mirror of `AgentListResponseSchema` in `agents.ts` — wraps the agent
// summary list because shared-types keeps the per-item schema atomic.
const AgentListResponseSchema = z.object({
    items: z.array(AgentSummarySchema),
    nextCursor: z.string().nullable(),
});
// Mirrors AgentInstruction.body constraint in docs/data-model.md §4.7.
const AgentInstructionPutRequestSchema = z.object({
    body: z.string().min(1).max(16384),
});
function basePath(tenantId) {
    return `/v1/admin/tenants/${encodeURIComponent(tenantId)}/agents`;
}
function agentPath(tenantId, agentId) {
    return `${basePath(tenantId)}/${encodeURIComponent(agentId)}`;
}
function sessionPath(tenantId, sessionId) {
    return `/v1/admin/tenants/${encodeURIComponent(tenantId)}/sessions/${encodeURIComponent(sessionId)}`;
}
export function createAdminTenantAgentsResource(http) {
    return {
        list: (tenantId, query) => http.request({
            method: 'GET',
            path: basePath(tenantId),
            query: query,
            responseSchema: AgentListResponseSchema,
        }),
        create: (tenantId, body) => http.request({
            method: 'POST',
            path: basePath(tenantId),
            body,
            responseSchema: AgentSummarySchema,
        }),
        get: (tenantId, agentId) => http.request({
            method: 'GET',
            path: agentPath(tenantId, agentId),
            responseSchema: AgentDetailSchema,
        }),
        update: (tenantId, agentId, body) => http.request({
            method: 'PATCH',
            path: agentPath(tenantId, agentId),
            body,
            responseSchema: AgentDetailSchema,
        }),
        updateGuardrails: (tenantId, agentId, body) => http.request({
            method: 'PATCH',
            path: `${agentPath(tenantId, agentId)}/guardrails`,
            body: AgentGuardrailPromptUpdateRequestSchema.parse(body),
            responseSchema: AgentDetailSchema,
        }),
        remove: async (tenantId, agentId) => {
            await http.request({
                method: 'DELETE',
                path: agentPath(tenantId, agentId),
                responseSchema: NoContentSchema,
            });
        },
        activate: (tenantId, agentId) => http.request({
            method: 'POST',
            path: `${agentPath(tenantId, agentId)}/activate`,
            responseSchema: AgentSchema,
        }),
        deactivate: (tenantId, agentId) => http.request({
            method: 'POST',
            path: `${agentPath(tenantId, agentId)}/deactivate`,
            responseSchema: AgentSchema,
        }),
        activateLanguage: (tenantId, agentId, lang) => http.request({
            method: 'POST',
            path: `${agentPath(tenantId, agentId)}/languages/${encodeURIComponent(lang)}/activate`,
            responseSchema: AgentSchema,
        }),
        deactivateLanguage: (tenantId, agentId, lang) => http.request({
            method: 'POST',
            path: `${agentPath(tenantId, agentId)}/languages/${encodeURIComponent(lang)}/deactivate`,
            responseSchema: AgentSchema,
        }),
        getInstruction: (tenantId, agentId, lang) => http.request({
            method: 'GET',
            path: `${agentPath(tenantId, agentId)}/instructions/${encodeURIComponent(lang)}`,
            responseSchema: AgentInstructionSchema,
        }),
        putInstruction: async (tenantId, agentId, lang, body) => {
            // Client-side validation mirrors the dashboard resource — fail fast
            // instead of waiting for a 400 from the server.
            const parsed = AgentInstructionPutRequestSchema.parse(body);
            await http.request({
                method: 'PUT',
                path: `${agentPath(tenantId, agentId)}/instructions/${encodeURIComponent(lang)}`,
                body: parsed,
                responseSchema: NoContentSchema,
            });
        },
        requestMediaUploadUrl: (tenantId, agentId, body) => http.request({
            method: 'POST',
            path: `${agentPath(tenantId, agentId)}/media/upload-url`,
            body,
            responseSchema: AgentMediaUploadResponseSchema,
        }),
        deleteBackground: async (tenantId, agentId, mediaId) => {
            await http.request({
                method: 'DELETE',
                path: `${agentPath(tenantId, agentId)}/backgrounds/${encodeURIComponent(mediaId)}`,
                responseSchema: NoContentSchema,
            });
        },
        getVoiceClone: (tenantId, agentId) => http.request({
            method: 'GET',
            path: `${agentPath(tenantId, agentId)}/voice-clone`,
            responseSchema: AgentVoiceCloneStatusResponseSchema,
        }),
        setupVoiceClone: (tenantId, agentId, body) => http.request({
            method: 'POST',
            path: `${agentPath(tenantId, agentId)}/voice-clone/setup`,
            body: AgentVoiceCloneSetupRequestSchema.parse(body),
            responseSchema: AgentVoiceCloneSetupResponseSchema,
        }),
        createVoiceCapture: (tenantId, agentId, body) => http.request({
            method: 'POST',
            path: `${agentPath(tenantId, agentId)}/voice-clone/capture`,
            body: AgentVoiceCaptureCreateRequestSchema.parse(body),
            responseSchema: AgentVoiceCaptureCreateResponseSchema,
        }),
        revokeVoiceCapture: (tenantId, agentId) => http.request({
            method: 'DELETE',
            path: `${agentPath(tenantId, agentId)}/voice-clone/capture`,
            responseSchema: AgentVoiceCaptureActionResponseSchema,
        }),
        activateVoiceClone: (tenantId, agentId) => http.request({
            method: 'POST',
            path: `${agentPath(tenantId, agentId)}/voice-clone/activate`,
            responseSchema: AgentVoiceCloneActionResponseSchema,
        }),
        deactivateVoiceClone: (tenantId, agentId) => http.request({
            method: 'POST',
            path: `${agentPath(tenantId, agentId)}/voice-clone/deactivate`,
            responseSchema: AgentVoiceCloneActionResponseSchema,
        }),
        listSessions: (tenantId, agentId, query) => http.request({
            method: 'GET',
            path: `${agentPath(tenantId, agentId)}/sessions`,
            query: query,
            responseSchema: SessionListResponseSchema,
        }),
        metrics: (tenantId, agentId, query) => http.request({
            method: 'GET',
            path: `${agentPath(tenantId, agentId)}/metrics`,
            query: query,
            responseSchema: AgentMetricsResponseSchema,
        }),
        getKnowledge: (tenantId, agentId) => http.request({
            method: 'GET',
            path: `${agentPath(tenantId, agentId)}/knowledge`,
            responseSchema: AgentKnowledgeListResponseSchema,
        }),
        updateKnowledgeConfig: (tenantId, agentId, body) => http.request({
            method: 'PATCH',
            path: `${agentPath(tenantId, agentId)}/knowledge/config`,
            body: AgentKnowledgeConfigUpdateRequestSchema.parse(body),
            responseSchema: AgentKnowledgeConfigSchema,
        }),
        createKnowledgeTextSource: (tenantId, agentId, body) => http.request({
            method: 'POST',
            path: `${agentPath(tenantId, agentId)}/knowledge/sources`,
            body: AgentKnowledgeTextSourceCreateRequestSchema.parse(body),
            responseSchema: AgentKnowledgeSourceResponseSchema,
        }),
        getKnowledgeSource: (tenantId, agentId, sourceId) => http.request({
            method: 'GET',
            path: `${agentPath(tenantId, agentId)}/knowledge/sources/${encodeURIComponent(sourceId)}`,
            responseSchema: AgentKnowledgeSourceDetailResponseSchema,
        }),
        updateKnowledgeSource: (tenantId, agentId, sourceId, body) => http.request({
            method: 'PATCH',
            path: `${agentPath(tenantId, agentId)}/knowledge/sources/${encodeURIComponent(sourceId)}`,
            body: AgentKnowledgeSourceUpdateRequestSchema.parse(body),
            responseSchema: AgentKnowledgeSourceResponseSchema,
        }),
        requestKnowledgeFileUploadUrl: (tenantId, agentId, body) => http.request({
            method: 'POST',
            path: `${agentPath(tenantId, agentId)}/knowledge/files/upload-url`,
            body: AgentKnowledgeFileSourceUploadRequestSchema.parse(body),
            responseSchema: AgentKnowledgeFileSourceUploadResponseSchema,
        }),
        deleteKnowledgeSource: async (tenantId, agentId, sourceId) => {
            await http.request({
                method: 'DELETE',
                path: `${agentPath(tenantId, agentId)}/knowledge/sources/${encodeURIComponent(sourceId)}`,
                responseSchema: NoContentSchema,
            });
        },
        getSession: (tenantId, sessionId) => http.request({
            method: 'GET',
            path: sessionPath(tenantId, sessionId),
            responseSchema: SessionDetailResponseSchema,
        }),
        deleteSession: async (tenantId, sessionId) => {
            await http.request({
                method: 'DELETE',
                path: sessionPath(tenantId, sessionId),
                responseSchema: NoContentSchema,
            });
        },
    };
}
//# sourceMappingURL=tenantAgents.js.map